import { Button, ResponsiveTable, useTranslation } from '@hbs-us/ui';
import { Trash2 } from 'lucide-react';

import type { MoneyFormatters } from '../../_money/useMoneyFormatters.js';

/**
 * Editable estimate line-items table (the create/edit counterpart to the
 * read-only `EstimateLineItemsTable`). Add/remove rows; each row edits
 * description, quantity, unit, base cost, and upcharge %, with a read-only
 * computed sell price + line total (display only — the server recomputes
 * authoritatively via the shared `normalizeLineItems`/`calculateTotals` helpers).
 *
 * Token-only styling (no hex — BRAND-06 / lint:no-hex); every control is
 * keyboard-operable with a visible or `aria-label`-ed label (A11Y-01). The sell
 * price uses the SAME rule as the server: sell = base × (1 + upcharge/100) when
 * apply-upcharge is on; line total = sell × quantity.
 *
 * `ResponsiveTable` reflows each row to a card of labelled inputs below `md`. At
 * 390px the eight columns squeezed the description input to 86px; stacked it is
 * 258px. The trade is height — one line item is a full phone screen. `md`+ is
 * unchanged.
 */

export interface EstimateLineRow {
  description: string;
  quantity: string;
  unit: string;
  baseCost: string;
  upchargePercent: string;
  applyUpcharge: boolean;
}

export function newLineRow(): EstimateLineRow {
  return {
    description: '',
    quantity: '',
    unit: '',
    baseCost: '',
    upchargePercent: '',
    applyUpcharge: false,
  };
}

/** The server's `roundMoney`: the same `toFixed(2)`, so the preview and the saved estimate agree to the cent. */
function roundMoney(value: number): number {
  return Number(value.toFixed(2));
}

/** Sell unit price for a row (mirrors the server `calculateUnitPrice`, rounded to the cent). */
export function rowSellPrice(row: EstimateLineRow): number {
  const base = Number(row.baseCost) || 0;
  const upcharge = Number(row.upchargePercent) || 0;
  if (!row.applyUpcharge) return roundMoney(base);
  return roundMoney(base * (1 + upcharge / 100));
}

/** Line total for a row: the ROUNDED sell price × quantity, as the server stores `line_total`. */
export function rowLineTotal(row: EstimateLineRow): number {
  return roundMoney(rowSellPrice(row) * (Number(row.quantity) || 0));
}

const INPUT_CLASS =
  'h-10 w-full rounded-md border border-input bg-background px-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background';
const NUMERIC_TD = 'px-2 py-2 text-right font-mono text-foreground text-sm';

export function EstimateLineItemsEditor({
  rows,
  onChange,
  fmt,
}: {
  rows: EstimateLineRow[];
  onChange: (rows: EstimateLineRow[]) => void;
  fmt: MoneyFormatters;
}) {
  const { t } = useTranslation();

  const updateRow = (index: number, patch: Partial<EstimateLineRow>) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };
  const addRow = () => {
    onChange([...rows, newLineRow()]);
  };

  return (
    <div className="flex flex-col gap-3">
      <ResponsiveTable>
        <table className="w-full border-collapse">
          <caption className="sr-only">{t('estimates:lineItems.caption')}</caption>
          <thead>
            <tr className="border-border border-b">
              <th
                scope="col"
                className="px-2 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                {t('estimates:lineItems.description')}
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                {t('estimates:lineItems.quantity')}
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                {t('estimates:lineItems.unit')}
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                {t('estimates:lineItems.baseCost')}
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                {t('estimates:lineItems.upcharge')}
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                {t('estimates:lineItems.sellUnitPrice')}
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                {t('estimates:lineItems.lineTotal')}
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                <span className="sr-only">{t('estimates:lineItemsEditor.rowActions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              // Rows are index-keyed: this is a positional editor (add/remove) with
              // no stable id until persisted, so the index IS the identity here.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional editor rows
              <tr key={index} className="border-border/60 border-b last:border-b-0">
                <td className="px-2 py-2">
                  <input
                    type="text"
                    aria-label={t('estimates:lineItemsEditor.descriptionFor', { n: index + 1 })}
                    value={row.description}
                    onChange={(e) => updateRow(index, { description: e.target.value })}
                    className={INPUT_CLASS}
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    aria-label={t('estimates:lineItemsEditor.quantityFor', { n: index + 1 })}
                    value={row.quantity}
                    onChange={(e) => updateRow(index, { quantity: e.target.value })}
                    className={`${INPUT_CLASS} text-right font-mono`}
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="text"
                    aria-label={t('estimates:lineItemsEditor.unitFor', { n: index + 1 })}
                    value={row.unit}
                    onChange={(e) => updateRow(index, { unit: e.target.value })}
                    className={INPUT_CLASS}
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    aria-label={t('estimates:lineItemsEditor.baseCostFor', { n: index + 1 })}
                    value={row.baseCost}
                    onChange={(e) => updateRow(index, { baseCost: e.target.value })}
                    className={`${INPUT_CLASS} text-right font-mono`}
                  />
                </td>
                <td className="px-2 py-2">
                  <div className="flex items-center justify-end gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      aria-label={t('estimates:lineItemsEditor.upchargeFor', { n: index + 1 })}
                      value={row.upchargePercent}
                      onChange={(e) => updateRow(index, { upchargePercent: e.target.value })}
                      className={`${INPUT_CLASS} min-w-0 text-right font-mono`}
                    />
                    <label className="flex shrink-0 items-center gap-1 whitespace-nowrap text-muted-foreground text-xs">
                      <input
                        type="checkbox"
                        aria-label={t('estimates:lineItemsEditor.applyUpchargeFor', {
                          n: index + 1,
                        })}
                        checked={row.applyUpcharge}
                        onChange={(e) => updateRow(index, { applyUpcharge: e.target.checked })}
                      />
                      {t('estimates:lineItemsEditor.apply')}
                    </label>
                  </div>
                </td>
                <td className={NUMERIC_TD}>{fmt.formatCurrency(rowSellPrice(row))}</td>
                <td className={`${NUMERIC_TD} font-medium`}>
                  {fmt.formatCurrency(rowLineTotal(row))}
                </td>
                <td className="px-2 py-2 text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(index)}
                    disabled={rows.length <= 1}
                    aria-label={t('estimates:lineItemsEditor.removeRow', { n: index + 1 })}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ResponsiveTable>
      <div>
        <Button type="button" variant="secondary" onClick={addRow}>
          {t('estimates:lineItemsEditor.addRow')}
        </Button>
      </div>
    </div>
  );
}
